import { useEffect } from "react";

import { isTypingTarget } from "../lib/dom";
import { parseCoordinates, type Point } from "../lib/coords";

/**
 * Paste and go: the real entrance to a district.
 *
 * Someone sends you «50.286, 31.313» — or a Google Maps link — and the whole interaction
 * should be Ctrl+V. That is ONE action, against two for the old always-present panel
 * (click the field, press Enter) and three through a collapsed search box.
 *
 * It stays quiet unless it is sure. Pasting outside a text field is not a search that
 * failed — it is usually not a search at all — so text that does not parse as a pair does
 * nothing at all: no flight, no error, no stolen clipboard. `parseCoordinates` is the same
 * measured parser the search box uses (lib/coords.ts, twelve formats under test), so what
 * the box accepts and what a paste accepts can never drift apart.
 */
export const useCoordinatePaste = (onFound: (point: Point) => void): void => {
  useEffect(() => {
    const onPaste = (event: ClipboardEvent): void => {
      if (isTypingTarget(event.target)) return;
      const text = event.clipboardData?.getData("text");
      if (!text) return;
      const parsed = parseCoordinates(text);
      if (!parsed.ok) return;
      event.preventDefault();
      onFound(parsed.point);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [onFound]);
};
